/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { ICondition, IProcedure } from '@ahryman40k/ts-fhir-types/lib/R4';
import { useRef } from 'react';
import clsx from 'clsx';

import { useAppSelector } from '../../../../app/hooks';
import { DocumentVersionType, fhirPatientDataTypes } from '../../algorithm/types';
import FHIRAccordion from '../../Fhir/FHIRAccordion';
import { selectFhirMedicalHx, selectFhirSurgicalHx } from '../../Fhir/fhirSlice';
import { formatDate } from '../../utils';
import styles from './PastMedicalHxContainer.module.scss';
import { CollapsibleInternalContainer } from '../CollapsibleInternalContainer';
import { showPatientProblemListAndPastMedicalHx } from '../../permissions';
import { getIcd10Codes } from '../../Fhir/icd10';
import { getCodeableConceptText } from '../../Fhir/codeableConcept';

export function PastMedicalHxContainer({
  documentVersion,
  medicalHx: medicalHxOverride,
  surgicalHx: surgicalHxOverride,
  defaultCollapsed = true,
}: {
  documentVersion?: DocumentVersionType;
  medicalHx?: ICondition[];
  surgicalHx?: IProcedure[];
  defaultCollapsed?: boolean;
}) {
  const divRef = useRef<HTMLDivElement>(null);
  const storedMedicalHx = useAppSelector(selectFhirMedicalHx);
  const storedSurgicalHx = useAppSelector(selectFhirSurgicalHx);
  const medicalHx_ = medicalHxOverride ?? storedMedicalHx;
  const surgicalHx_ = surgicalHxOverride ?? storedSurgicalHx;

  const enabled =
    medicalHxOverride !== undefined || surgicalHxOverride !== undefined || showPatientProblemListAndPastMedicalHx();
  if (!enabled) {
    return null;
  }

  if (!(!!medicalHx_ && medicalHx_.length > 0) && !(!!surgicalHx_ && surgicalHx_.length > 0)) {
    return null;
  }

  const medicalHx = !!medicalHx_
    ? [...medicalHx_].sort((a, b) => {
        const dateA = parseMedicalHxDate(a);
        const dateB = parseMedicalHxDate(b);

        if (dateA && dateB) return dateB.getTime() - dateA.getTime();
        if (dateA || dateB) return dateA ? -1 : 1;
        return a.onsetString ? -1 : b.onsetString ? 1 : 0;
      })
    : [];

  const surgicalHx = !!surgicalHx_
    ? [...surgicalHx_].sort((a, b) => {
        const dateA = parseSurgicalHxDate(a);
        const dateB = parseSurgicalHxDate(b);

        if (dateA && dateB) return dateB.getTime() - dateA.getTime();
        if (dateA || dateB) return dateA ? -1 : 1;
        return 0;
      })
    : [];

  const fhirDataType = fhirPatientDataTypes.find((x) => x.id === 'Medical History');
  const hasBothSections = medicalHx.length > 0 && surgicalHx.length > 0;

  return (
    <FHIRAccordion
      heading={fhirDataType?.value}
      divRef={divRef}
      defaultCollapsed={defaultCollapsed}
      mixpanelWidgetName="past_medical_history"
      documentVersion={documentVersion}
    >
      <div ref={divRef} className={styles.container}>
        {medicalHx?.length > 0 && (
          <CollapsibleInternalContainer
            title="Medical"
            localStorageName="past-history-medical-section"
            collapsible={hasBothSections}
          >
            <table className={styles.medicalHxWidgets}>
              <tbody>
                {medicalHx.map((res, index) => (
                  <MedicalHxWidget res={res} key={index} />
                ))}
              </tbody>
            </table>
          </CollapsibleInternalContainer>
        )}
        {surgicalHx?.length > 0 && (
          <CollapsibleInternalContainer
            title="Surgical"
            localStorageName="past-history-surgical-section"
            collapsible={hasBothSections}
          >
            <table className={styles.medicalHxWidgets}>
              <tbody>
                {surgicalHx.map((res, index) => (
                  <SurgicalHxWidget res={res} key={index} />
                ))}
              </tbody>
            </table>
          </CollapsibleInternalContainer>
        )}
      </div>
    </FHIRAccordion>
  );
}

const parseMedicalHxDate = (record: ICondition): Date | null => {
  if (record.onsetDateTime) return new Date(record.onsetDateTime);
  if (record.onsetPeriod?.start) return new Date(record.onsetPeriod.start);
  if (record.onsetPeriod?.end) return new Date(record.onsetPeriod.end);
  if (record.onsetString) {
    const parsedDate = Date.parse(record.onsetString);
    if (!isNaN(parsedDate)) return new Date(parsedDate);
  }
  return null;
};

export const getProcedureDate = (procedure: IProcedure) => {
  return procedure.performedDateTime || procedure.performedPeriod?.start;
};

export const getProcedureDateShortString = (procedure: IProcedure): string => {
  const procedureDate = getProcedureDate(procedure);
  return procedureDate ? formatDate(procedureDate, 'MM/dd/yyyy') : 'Date Unknown';
};

export const getProcedureDateLongString = (procedure: IProcedure): string => {
  if (procedure.performedDateTime) {
    return formatDate(procedure.performedDateTime);
  }
  if (procedure.performedPeriod) {
    const start = procedure.performedPeriod.start ? formatDate(procedure.performedPeriod.start) : 'Unknown Start Date';
    const end = procedure.performedPeriod.end ? formatDate(procedure.performedPeriod.end) : 'Unknown End Date';
    return `${start} - ${end}`;
  }
  return 'Date Unknown';
};

const parseSurgicalHxDate = (record: IProcedure): Date | null => {
  const procedureDate = getProcedureDate(record);
  if (procedureDate) {
    const parsedDate = Date.parse(procedureDate);
    if (!isNaN(parsedDate)) return new Date(parsedDate);
  }
  return null;
};

export function MedicalHxWidget({ res }: { res: ICondition }) {
  // Refer to https://fhir.epic.com/Specifications?api=10314
  // confirmed - positive medical history
  // refuted -  pertinent negative medical history
  const verificationStatus =
    res.verificationStatus?.text || res.verificationStatus?.coding?.find((coding) => coding.code)?.code;
  if (verificationStatus && verificationStatus.toLowerCase() !== 'confirmed') {
    return null;
  }

  // problem name with note
  const conditionName = getCodeableConceptText(res.code);
  const noteText = res.note
    ?.map((note) => note.text?.trim())
    .filter(Boolean)
    .join('; ');
  const name = [conditionName, noteText && `(${noteText})`].filter(Boolean).join(' ');
  const icd10Codes = getIcd10Codes(res.code);

  let onsetDateTime = '';

  // onsetDateTime  - The date when the medical problem began for the patient.
  // onsetPeriod  - The date range when the medical problem began for the patient. Applicable for dates saved as fuzzy dates.
  // onsetString  - The date when the medical problem began for the patient.  Applicable for dates saved as a string instead of a date format.
  if (!!res.onsetDateTime) {
    onsetDateTime = formatDate(res.onsetDateTime!, 'MM/dd/yyyy');
  } else if (!!res.onsetPeriod && (!!res.onsetPeriod.start || !!res.onsetPeriod.end)) {
    const startDate = !!res.onsetPeriod.start ? new Date(res.onsetPeriod.start) : undefined;
    const endDate = !!res.onsetPeriod.end ? new Date(res.onsetPeriod.end) : undefined;

    if (!!startDate && !!endDate) {
      if (startDate.getFullYear() === endDate.getFullYear()) onsetDateTime = startDate.getFullYear().toString();
      else onsetDateTime = `${startDate.getFullYear()} - ${endDate.getFullYear()}`;
    } else if (!!startDate) {
      onsetDateTime = startDate.getFullYear().toString();
    } else if (!!endDate) {
      onsetDateTime = endDate.getFullYear().toString();
    }
  } else if (!!res.onsetString) {
    onsetDateTime = res.onsetString.trim();
  } else if (res.onsetAge?.value !== undefined) {
    onsetDateTime = `Age ${res.onsetAge.value}`;
  }

  return (
    <tr key={res.id} className={styles.medicalHxWidget}>
      <td className={clsx(styles.dateRange, !!onsetDateTime && styles.onsetDateTime)}>
        {onsetDateTime || 'Date Unknown'}
      </td>
      <td className={styles.hxContent}>
        {name}
        {icd10Codes.length > 0 && <span className={styles.icd10Code}>ICD-10: {icd10Codes.join(', ')}</span>}
      </td>
    </tr>
  );
}

export function SurgicalHxWidget({ res }: { res: IProcedure }) {
  if (res.status?.toLowerCase() !== 'completed') {
    return null;
  }
  const procedureDate = getProcedureDate(res);
  const icd10Codes = getIcd10Codes(res.code);

  return (
    <tr key={res.id} className={styles.medicalHxWidget}>
      <td className={clsx(styles.dateRange, !!procedureDate && styles.onsetDateTime)}>
        {getProcedureDateShortString(res)}
      </td>
      <td className={styles.hxContent}>
        {getCodeableConceptText(res.code)}
        {icd10Codes.length > 0 && <span className={styles.icd10Code}>ICD-10: {icd10Codes.join(', ')}</span>}
      </td>
    </tr>
  );
}
